import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Flame } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { DCard, EmptyState, LineChart, HeroCard, LoadError } from "@/components/dryvn";
import {
  AddPanel,
  LifeField,
  LifeInput,
  LifeTextarea,
  SubmitButton,
} from "@/components/life-forms";
// Screen migration (2026-07-05): data via adapter-backed hooks.
// 2026-07-15: metric-log form + honest empty/error states.
import type { HealthMetric } from "@/data/types";
import { useAddHealthRecord, useHabits, useHealthMetrics } from "@/lib/queries";

export const Route = createFileRoute("/health")({
  head: () => ({
    meta: [
      { title: "Health — Dryvn Life" },
      {
        name: "description",
        content: "Habits, streaks, and health records — gentle and never clinical.",
      },
    ],
  }),
  component: Health,
});

const dayLabels = ["M", "T", "W", "T", "F", "S", "S"];

/** Readings a metric sparkline plots. Per LIST_ARCHIVE_POLICY_2026-08. */
const CHART_WINDOW = 30;

function Health() {
  const { data: habits, isError: habitsError, error: habitsErr } = useHabits();
  const { data: healthMetrics, isError: metricsError, error: metricsErr } = useHealthMetrics();
  const [tab, setTab] = useState<"habits" | "records">("habits");

  // Optimistic metrics list so a logged record shows immediately (demo + live).
  const [metrics, setMetrics] = useState<HealthMetric[]>(healthMetrics);
  useEffect(() => setMetrics(healthMetrics), [healthMetrics]);

  const [metricType, setMetricType] = useState("");
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("");
  const [notes, setNotes] = useState("");
  const [submitError, setSubmitError] = useState(false);
  const addRecord = useAddHealthRecord();

  const submit = (close: () => void) => {
    const label = metricType.trim();
    const num = Number(value);
    if (!label || !value.trim() || Number.isNaN(num)) return;
    // Optimistic insert for instant feedback; snapshot so a failed save can
    // roll it back instead of leaving a reading that never persisted.
    const snapshot = metrics;
    setMetrics((prev) => {
      const idx = prev.findIndex((m) => m.label.toLowerCase() === label.toLowerCase());
      if (idx >= 0) {
        const next = prev.slice();
        const m = next[idx]!;
        next[idx] = {
          ...m,
          value: String(num),
          unit: unit.trim() || m.unit,
          history: [...m.history, num],
        };
        return next;
      }
      return [...prev, { label, value: String(num), unit: unit.trim(), history: [num] }];
    });
    setSubmitError(false);
    addRecord.mutate(
      {
        metricType: label,
        valueNumeric: num,
        unit: unit.trim() || undefined,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          setMetricType("");
          setValue("");
          setUnit("");
          setNotes("");
          close();
        },
        onError: () => {
          setMetrics(snapshot);
          setSubmitError(true);
        },
      },
    );
  };

  return (
    <AppShell>
      <HeroCard
        eyebrow="Health"
        title={
          <>
            Small{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              habits
            </em>
            , steady numbers.
          </>
        }
      >
        <p
          className="max-w-xl text-sm italic leading-relaxed"
          style={{ color: "var(--dryvn-ink)" }}
        >
          We track, we remind, we flag, we refer. Your doctor always makes the call.
        </p>
      </HeroCard>

      <div
        className="mt-8 mb-6 inline-flex rounded-full p-1"
        style={{ backgroundColor: "var(--dryvn-page-tint)" }}
      >
        {(["habits", "records"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="rounded-full px-4 py-1.5 text-sm capitalize transition-colors"
            style={{
              backgroundColor: tab === t ? "var(--dryvn-card)" : "transparent",
              color: tab === t ? "var(--dryvn-ink)" : "var(--dryvn-grey)",
              boxShadow: tab === t ? "var(--shadow-soft)" : "none",
              fontWeight: tab === t ? 500 : 400,
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "habits" && (
        <>
          {habitsError && <LoadError error={habitsErr} />}
          {!habitsError && habits.length === 0 && (
            <EmptyState
              title="No habits tracked yet. Start one by texting your Dryvn number — it'll build your streak here."
              smsExamples={["track water", "did my walk today"]}
            />
          )}
          {habits.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              {habits.map((h) => (
                <DCard key={h.id}>
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
                      {h.name}
                    </h3>
                    <span
                      className="flex items-center gap-1 text-xs"
                      style={{ color: "var(--dryvn-grey)" }}
                    >
                      <Flame className="h-3.5 w-3.5" style={{ color: "var(--dryvn-warm-deep)" }} />
                      {h.streak}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    {h.week.map((done, i) => (
                      <div key={i} className="flex flex-col items-center gap-1.5">
                        <span
                          className="h-6 w-6 rounded-full"
                          style={{
                            backgroundColor: done
                              ? "var(--dryvn-primary)"
                              : "var(--dryvn-page-tint)",
                          }}
                        />
                        <span className="text-[10px]" style={{ color: "var(--dryvn-grey)" }}>
                          {dayLabels[i]}
                        </span>
                      </div>
                    ))}
                  </div>
                </DCard>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "records" && (
        <div className="space-y-6">
          <AddPanel label="Log a reading">
            {(close) => (
              <form
                className="space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  submit(close);
                }}
              >
                <div className="grid grid-cols-2 gap-3">
                  <LifeField label="What">
                    <LifeInput
                      value={metricType}
                      onChange={(e) => setMetricType(e.target.value)}
                      placeholder="weight, steps, bp…"
                    />
                  </LifeField>
                  <LifeField label="Value">
                    <LifeInput
                      type="number"
                      step="any"
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      placeholder="e.g. 72"
                    />
                  </LifeField>
                </div>
                <LifeField label="Unit (optional)">
                  <LifeInput
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    placeholder="kg, steps, mmHg…"
                  />
                </LifeField>
                <LifeField label="Note (optional)">
                  <LifeTextarea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Anything worth remembering"
                  />
                </LifeField>
                <SubmitButton disabled={!metricType.trim() || !value.trim()}>
                  Save reading
                </SubmitButton>
                {submitError && (
                  <p className="text-xs" style={{ color: "var(--dryvn-warm-deep)" }}>
                    We couldn't save that just now — give it another try in a moment.
                  </p>
                )}
              </form>
            )}
          </AddPanel>

          {metricsError && <LoadError error={metricsErr} />}
          {!metricsError && metrics.length === 0 && (
            <EmptyState
              title="No readings logged yet. Add one above, or text your Dryvn number."
              smsExamples={["weight 72kg", "bp 120/80 this morning"]}
            />
          )}
          {metrics.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              {metrics.map((m) => (
                <DCard key={m.label}>
                  <p
                    className="font-mono text-[10px] uppercase tracking-wider"
                    style={{ color: "var(--dryvn-grey)" }}
                  >
                    {m.label}
                  </p>
                  <p className="mt-1 font-display text-3xl" style={{ color: "var(--dryvn-ink)" }}>
                    {m.value}
                    <span
                      className="ml-1 text-xs font-normal"
                      style={{ color: "var(--dryvn-grey)" }}
                    >
                      {m.unit}
                    </span>
                  </p>
                  <div className="mt-3">
                    {/* The chart caps its own window: a year of daily weigh-ins
                        compresses into an unreadable hairline, and the card is
                        a "how am I trending" glance, not the archive. Sliced
                        here rather than in adapt.ts so other callers of
                        `history` keep the full series. */}
                    <LineChart data={m.history.slice(-CHART_WINDOW)} />
                  </div>
                  {m.history.length > CHART_WINDOW && (
                    <p
                      className="mt-1 font-mono text-[10px]"
                      style={{ color: "var(--dryvn-grey)" }}
                    >
                      Last {CHART_WINDOW} of {m.history.length} readings
                    </p>
                  )}
                </DCard>
              ))}
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
